import './TeamSelectionPage.css';
import { StashButton } from '../components/StashButton';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainMenuFeeds } from '../components/MainMenuFeeds';
import { SteamBotPanel } from '../components/SteamBotPanel';
import axios from 'axios';
import { Search, Plus, KeyRound, LayoutDashboard, LogOut, AlertTriangle, X, Shield, Settings, Users, Trophy, ChevronRight, ChevronLeft, Crown, User, Target, Crosshair, Sparkles, Chrome, Clock, Play, Gamepad2, Server, Film, Bell, CheckCheck, MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { RafflePointsIcon } from '../components/RafflePointsIcon';
import { useCountdown } from '../components/WeeklyRaffleBoard';
import { cn } from '../lib/utils';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useHeroVideo } from '../hooks/useHeroVideo';
import { WelcomeWizard } from '../components/WelcomeWizard';
import { useBroadcast } from '../hooks/useBroadcast';
import { useSmartPolling } from '../hooks/useSmartPolling';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const MEDIA_ORIGIN = API.replace(/\/api\/?$/, '');
/** Clip URLs are usually absolute S3 links, but locally stored ones are
 *  `/uploads/...` and need the media origin — same rule as the Highlights view. */
function resolveVideoUrl(url: string) {
    return url.startsWith('/uploads/') ? `${MEDIA_ORIGIN}${url}` : url;
}

type ActivePanel = null | 'add' | 'teams';

interface TeamDetail {
    teamId: string;
    members: { id: string; username: string; ingameName?: string; avatarUrl?: string; teamRole?: string }[];
    activeSeasons: number;
    completedLeagues: number;
    league?: { name: string; wins: number; losses: number; draws: number };
}

interface TeamNotification {
    id: string;
    teamId: string;
    type: string;
    title: string;
    body: string;
    link?: string;
    read: boolean;
    createdAt: string;
}

export function TeamSelectionPage() {
    const { user, token, refreshUser, logout } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [teamFaceitUrl, setTeamFaceitUrl] = useState('');
    const [joinCode, setJoinCode] = useState('');
    const [error, setError] = useState('');
    const [isSwitching, setIsSwitching] = useState(false);
    const [tunnelTransition, setTunnelTransition] = useState(false);
    const [teamToLeave, setTeamToLeave] = useState<any | null>(null);
    const [isLeaving, setIsLeaving] = useState(false);
    const [activePanel, setActivePanel] = useState<ActivePanel>(null);
    const [teamDetails, setTeamDetails] = useState<Record<string, TeamDetail>>({});
    const [_loadingDetails, setLoadingDetails] = useState(false);
    const [iconTooltip, setIconTooltip] = useState<{ x: number; y: number; label: string; color: string } | null>(null);
    const [teamOrder, setTeamOrder] = useState<string[]>([]);
    const [currentTeamIndex, setCurrentTeamIndex] = useState(0);
    const { videoRef: heroVideoRef } = useHeroVideo();
    /** Live Deathmatch rating for the hub tiles (Prefire/Crossfire ELO is per map now). */
    const [dmStats, setDmStats] = useState<{ elo: number; rank: number | null } | null>(null);
    // Raffle-points balance + when the next raffle closes (for the loot-case widget).
    const [rafflePoints, setRafflePoints] = useState<number | null>(null);
    /** Latest Clip of the Week winner for the hub card — null hides the card. */
    const [clipOfWeek, setClipOfWeek] = useState<{
        weekKey: string;
        title: string;
        mapName?: string | null;
        s3VideoUrl: string;
        likeCount: number;
        player: { id: string | null; name: string; avatarUrl: string | null };
    } | null>(null);
    const [clipModalOpen, setClipModalOpen] = useState(false);
    const [nextRaffleCloseAt, setNextRaffleCloseAt] = useState<string | null>(null);
    const raffleCountdown = useCountdown(nextRaffleCloseAt);
    const [faceitTeams, setFaceitTeams] = useState<any[]>([]);
    const [loadingFaceitTeams, setLoadingFaceitTeams] = useState(false);
    const [noFaceitAccount, setNoFaceitAccount] = useState(false);
    const [creatingTeamId, setCreatingTeamId] = useState<string | null>(null);
    const [showPremiumGate, setShowPremiumGate] = useState(false);
    const { broadcast } = useBroadcast();
    const [showNotificationFeed, setShowNotificationFeed] = useState(false);
    const [notificationFeedTab, setNotificationFeedTab] = useState<'team' | 'personal'>('team');
    const [teamNotifications, setTeamNotifications] = useState<TeamNotification[]>([]);
    const [personalNotifications, setPersonalNotifications] = useState<TeamNotification[]>([]);
    const notificationButtonRef = useRef<HTMLButtonElement>(null);
    const notificationPanelRef = useRef<HTMLDivElement>(null);

    // Welcome Wizard — show for first-time users (no teams, wizard not completed in DB)
    const [showWizard, setShowWizard] = useState(false);
    const [isCheckingWizard, setIsCheckingWizard] = useState(true);

    useEffect(() => {
        const hasTeams = (user?.teamMemberships || []).length > 0;
        if (hasTeams) { 
            setShowWizard(false); 
            setIsCheckingWizard(false);
            return; 
        }
        // No teams — check DB for onboarding status
        (async () => {
            try {
                const token = localStorage.getItem('token');
                const { data } = await axios.get(`${API}/auth/onboarding-status`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!data.onboardingDone) setShowWizard(true);
            } catch { /* ignore */ }
            finally {
                setIsCheckingWizard(false);
            }
        })();
    }, [user?.teamMemberships]);



    // Initialise teamOrder whenever memberships change
    useEffect(() => {
        setTeamOrder((prev) => {
            const ids = (user?.teamMemberships || []).map((m: any) => m.team.id);
            // Keep existing order for known ids, append new ones
            const kept = prev.filter((id) => ids.includes(id));
            const newIds = ids.filter((id: string) => !kept.includes(id));
            return [...kept, ...newIds];
        });
    }, [user?.teamMemberships]);




    // Fade-in when arriving from Dashboard via "Back to Menu"
    const [menuFadeIn] = useState(() => {
        if (sessionStorage.getItem('menuFadeIn') === 'true') {
            sessionStorage.removeItem('menuFadeIn');
            return true;
        }
        return false;
    });
    const [menuFadeReady, setMenuFadeReady] = useState(false);

    useEffect(() => {
        if (menuFadeIn) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setMenuFadeReady(true);
                });
            });
        } else {
            setMenuFadeReady(true);
        }
    }, [menuFadeIn]);

    // Always refresh user data on mount to get fresh team memberships
    useEffect(() => {
        refreshUser();
    }, []);

    const fetchNotifications = useCallback(async (signal?: AbortSignal) => {
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        const requestConfig = { headers, signal, timeout: 8000 };
        const [team, personal] = await Promise.all([
            axios.get<TeamNotification[]>(`${API}/notifications`, { ...requestConfig, params: { scope: 'team' } }),
            axios.get<TeamNotification[]>(`${API}/notifications`, { ...requestConfig, params: { scope: 'personal' } }),
        ]);
        setTeamNotifications(team.data);
        setPersonalNotifications(personal.data);
    }, [token]);

    useSmartPolling(fetchNotifications, {
        enabled: !!token,
        intervalMs: 60_000,
        jitterRatio: 0.1,
        maxBackoffMs: 240_000,
        restartKey: token,
    });

    useEffect(() => {
        if (!showNotificationFeed) return;

        const closeOnOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                !notificationPanelRef.current?.contains(target)
                && !notificationButtonRef.current?.contains(target)
            ) {
                setShowNotificationFeed(false);
            }
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setShowNotificationFeed(false);
        };

        document.addEventListener('mousedown', closeOnOutsideClick);
        window.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            window.removeEventListener('keydown', closeOnEscape);
        };
    }, [showNotificationFeed]);

    // Fetch the Deathmatch rating (ELO + live placement) on mount
    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/game-server/my-dm-stats`);
                if (res.data?.elo != null) setDmStats({ elo: res.data.elo, rank: res.data.rank ?? null });
            } catch { /* ignore */ }
        })();
    }, []);

    // Raffle-points balance + the next raffle to close (earliest future end date).
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/raffles`);
                setRafflePoints(data?.balance ?? 0);
                const now = Date.now();
                const next = (data?.raffles ?? [])
                    .filter((r: any) => r.phase !== 'evaluated' && new Date(r.endsAt).getTime() > now)
                    .sort((a: any, b: any) => new Date(a.endsAt).getTime() - new Date(b.endsAt).getTime())[0];
                setNextRaffleCloseAt(next?.endsAt ?? null);
            } catch { /* ignore */ }
        })();
    }, []);

    // Winner of the latest Clips of the Week release. The API returns
    // `clip: null` while the admin switch is off or no week produced a #1, so
    // there is no half-filled state to guard against here.
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/highlights/game/clip-of-the-week`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                setClipOfWeek(data?.clip ?? null);
            } catch { /* card simply stays hidden */ }
        })();
    }, []);

    useEffect(() => {
        if (!clipModalOpen) return;
        const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setClipModalOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [clipModalOpen]);

    // Eagerly fetch team details on mount for inline card display
    useEffect(() => {
        const mems = user?.teamMemberships || [];
        if (mems.length > 0 && Object.keys(teamDetails).length === 0) {
            fetchTeamDetails();
        }
    }, [user?.teamMemberships?.length]);

    // Fetch Faceit teams when Create or Join panel opens
    useEffect(() => {
        if (activePanel !== 'add' && !showWizard) return;
        let cancelled = false;
        setLoadingFaceitTeams(true);
        setFaceitTeams([]);
        setNoFaceitAccount(false);
        setCreatingTeamId(null);
        (async () => {
            try {
                const { data } = await axios.get(`${API}/teams/my-faceit-teams`);
                if (cancelled) return;
                setFaceitTeams(data.teams || []);
                setNoFaceitAccount(!!data.noFaceitAccount);
            } catch {
                if (!cancelled) setFaceitTeams([]);
            }
            if (!cancelled) setLoadingFaceitTeams(false);
        })();
        return () => { cancelled = true; };
    }, [activePanel, showWizard]);

    const handleCreateTeam = async () => {
        try {
            if (!teamFaceitUrl.trim()) return;

            // Premium gate: block non-premium users from creating additional teams
            const currentTeamCount = (user?.teamMemberships || []).filter(m => (m as any).leftReason !== 'PENDING_INVITE').length;
            if (currentTeamCount >= 1 && !user?.isPremium) {
                setError('You already have a team. Premium is required to manage multiple teams simultaneously.');
                return;
            }

            await axios.post(`${API}/teams`, {
                faceitUrl: teamFaceitUrl
            });
            await refreshUser();
            setTeamFaceitUrl('');
            // Show the Your Teams panel instead of navigating directly to dashboard
            collapsePanel();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to create team');
        }
    };

    const handleJoinTeam = async () => {
        try {
            if (!joinCode.trim()) return;

            // Premium gate: block non-premium users from joining additional teams
            const currentTeamCount = (user?.teamMemberships || []).filter(m => (m as any).leftReason !== 'PENDING_INVITE').length;
            if (currentTeamCount >= 1 && !user?.isPremium) {
                setError('You already have a team. Premium is required to manage multiple teams simultaneously.');
                return;
            }

            const res = await axios.post(`${API}/teams/join`, { code: joinCode });
            await refreshUser();
            setJoinCode('');
            collapsePanel();
            if (res.data?.teamId) {
                handleSwitchTeam(res.data.teamId);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to join team');
        }
    };

    const handleSwitchTeam = async (teamId: string) => {
        try {
            setIsSwitching(true);
            // Trigger visual transitions immediately
            setTunnelTransition(true);
            sessionStorage.setItem('dashboardFadeIn', 'true');
            sessionStorage.setItem('tunnelOverlayActive', 'true');

            // Perform network calls in the background while the visual transition runs
            const startTime = Date.now();
            await axios.patch(`${API}/teams/${teamId}/switch`);
            await refreshUser();
            
            const elapsed = Date.now() - startTime;
            // The tunnel transition takes 1500ms. If the API took less, wait the remainder.
            const remainingDelay = Math.max(0, 1500 - elapsed);
            
            setTimeout(() => {
                navigate(`/team/${teamId}/dashboard`);
                setIsSwitching(false);
            }, remainingDelay);
        } catch (err: any) {
            setError('Failed to switch team');
            setTunnelTransition(false);
            setIsSwitching(false);
        }
    };

    const handlePersonalSpace = () => {
        setTunnelTransition(true);
        sessionStorage.setItem('dashboardFadeIn', 'true');
        sessionStorage.setItem('tunnelOverlayActive', 'true');
        setTimeout(() => {
            navigate('/personal');
        }, 1500);
    };

    const unreadTeamNotificationCount = teamNotifications.filter(notification => !notification.read).length;
    const unreadPersonalNotificationCount = personalNotifications.filter(notification => !notification.read).length;
    const unreadNotificationCount = unreadTeamNotificationCount + unreadPersonalNotificationCount;

    const getNotificationTeamName = (teamId: string) => (
        (user?.teamMemberships || []).find(membership => membership.team.id === teamId)?.team.name
        || 'Team'
    );

    const formatNotificationAge = (createdAt: string) => {
        const ageInSeconds = Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000));
        if (ageInSeconds < 60) return 'Just now';
        if (ageInSeconds < 3600) return `${Math.floor(ageInSeconds / 60)}m ago`;
        if (ageInSeconds < 86400) return `${Math.floor(ageInSeconds / 3600)}h ago`;
        return `${Math.floor(ageInSeconds / 86400)}d ago`;
    };

    const markAllTeamNotificationsRead = async () => {
        setTeamNotifications(current => current.map(notification => ({ ...notification, read: true })));
        try {
            await axios.patch(`${API}/notifications/read-all`, {}, { params: { scope: 'team' } });
        } catch {
            void fetchNotifications().catch(() => { });
        }
    };

    const markAllPersonalNotificationsRead = async () => {
        setPersonalNotifications(current => current.map(notification => ({ ...notification, read: true })));
        try {
            await axios.patch(`${API}/notifications/read-all`, {}, { params: { scope: 'personal' } });
        } catch {
            void fetchNotifications().catch(() => { });
        }
    };

    const openTeamNotification = async (notification: TeamNotification) => {
        setTeamNotifications(current => current.map(item => (
            item.id === notification.id ? { ...item, read: true } : item
        )));

        try {
            await axios.patch(`${API}/notifications/${notification.id}/read`, {});

            if (notification.link) {
                await axios.patch(`${API}/teams/${notification.teamId}/switch`);
                await refreshUser();
                const target = notification.link.replace(/^\/+/, '');
                navigate(`/team/${notification.teamId}/${target}`);
            }
            setShowNotificationFeed(false);
        } catch (notificationError: unknown) {
            void fetchNotifications().catch(() => { });
            const message = axios.isAxiosError(notificationError)
                ? notificationError.response?.data?.message
                : null;
            toast.error(message || 'Failed to open notification');
        }
    };

    const openPersonalNotification = async (notification: TeamNotification) => {
        setPersonalNotifications(current => current.map(item => (
            item.id === notification.id ? { ...item, read: true } : item
        )));
        try {
            await axios.patch(`${API}/notifications/${notification.id}/read`, {});
            if (notification.link) navigate(notification.link);
            setShowNotificationFeed(false);
        } catch (notificationError: unknown) {
            void fetchNotifications().catch(() => { });
            const message = axios.isAxiosError(notificationError)
                ? notificationError.response?.data?.message
                : null;
            toast.error(message || 'Failed to open notification');
        }
    };

    const personalNotificationIcon = (type: string) => {
        if (type === 'FORUM_REPLY') return <MessageCircle size={16} />;
        if (type === 'WEEKLY_REWARDS') return <Trophy size={16} />;
        if (type === 'BADGE_AWARDED') return <Sparkles size={16} />;
        return <Bell size={16} />;
    };

    const handleLeaveTeam = async () => {
        if (!teamToLeave) return;
        try {
            setIsLeaving(true);
            await axios.post(`${API}/teams/${teamToLeave.id}/leave`);
            await refreshUser();
            setTeamToLeave(null);
            // If user has no teams left, collapse the panel back to the overview
            const remaining = (user?.teamMemberships || []).filter(m => m.team.id !== teamToLeave.id);
            if (remaining.length === 0) {
                collapsePanel();
            }
        } catch (err: any) {
            // The hub view has no inline error slot, so surface the server's reason
            // (e.g. "Owner cannot leave the team") as a toast as well.
            const message = err.response?.data?.message || t('teamSelection.failedToLeave');
            setError(message);
            toast.error(message);
            setTeamToLeave(null);
        } finally {
            setIsLeaving(false);
        }
    };

    const expandPanel = (panel: ActivePanel) => {
        setError('');
        // If switching from one expanded panel to another, collapse first then expand
        if (activePanel && activePanel !== panel) {
            setActivePanel(null);
            setJoinCode('');
            setTeamFaceitUrl('');
            setTimeout(() => {
                setActivePanel(panel);
                if (panel === 'teams') {
                    fetchTeamDetails();
                }
            }, 400);
            return;
        }
        setActivePanel(panel);
        if (panel === 'teams') {
            fetchTeamDetails();
        }
    };

    const fetchTeamDetails = async () => {
        if (!memberships.length) return;
        setLoadingDetails(true);
        try {
            const details: Record<string, TeamDetail> = {};
            for (const m of memberships) {
                try {
                    const { data } = await axios.get(`${API}/teams/${m.team.id}/basic-summary`);
                    
                    const leagues = data.leagues || [];
                    const activeLeague = leagues.find((l: any) => !l.isClosed);
                    const activeSeasons = leagues.filter((l: any) => !l.isClosed).length;
                    const completedLeagues = leagues.filter((l: any) => l.isClosed).length;

                    details[m.team.id] = {
                        teamId: m.team.id,
                        members: (data.members || []).map((mem: any) => ({
                            id: mem.id,
                            username: mem.username,
                            ingameName: mem.ingameName,
                            avatarUrl: mem.avatarUrl,
                            teamRole: mem.teamRole || mem.role,
                        })),
                        activeSeasons,
                        completedLeagues,
                        league: activeLeague ? (() => {
                            const matches = activeLeague.matches || [];
                            let wins = 0, losses = 0, draws = 0;
                            for (const match of matches) {
                                if (!match.result) continue;
                                const parts = match.result.split('-').map(Number);
                                if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) continue;
                                if (parts[0] > parts[1]) wins++;
                                else if (parts[0] < parts[1]) losses++;
                                else draws++;
                            }
                            return { name: activeLeague.name || 'League', wins, losses, draws };
                        })() : undefined,
                    };
                } catch {
                    details[m.team.id] = { teamId: m.team.id, members: [], activeSeasons: 0, completedLeagues: 0 };
                }
            }
            setTeamDetails(details);
        } catch {
            // ignore
        } finally {
            setLoadingDetails(false);
        }
    };

    const collapsePanel = () => {
        setError('');
        setActivePanel(null);
        setJoinCode('');
        setTeamFaceitUrl('');
    };

    const memberships = (user?.teamMemberships || []).filter(m => (m as any).leftReason !== 'PENDING_INVITE');
    const hasTeams = memberships.length > 0;
    const isMaxTeams = memberships.length >= 3;
    const canAddTeam = !hasTeams || (user?.isPremium && !isMaxTeams);
    const needsPremiumForMore = hasTeams && !user?.isPremium;
    const showButtons = !isMaxTeams; // hide only at absolute max (3)

    return (
        <div className="hub-page min-h-screen bg-black relative overflow-hidden">
            {!showWizard && !tunnelTransition && <SteamBotPanel />}
            {menuFadeIn && (
                <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 300, backgroundColor: '#000', opacity: menuFadeReady ? 0 : 0.7, transition: 'opacity 5s cubic-bezier(0.25, 0.8, 0.25, 1)' }} />
            )}
            <div className="fixed inset-0 z-[200] pointer-events-none" style={{ opacity: tunnelTransition ? 1 : 0, transition: 'opacity 1.3s cubic-bezier(0.4, 0, 0.2, 1)', backgroundColor: '#000' }} />
            {!showWizard && (
                <>
                    <div className={`hub-stash fixed z-[220] flex items-center justify-center transition-all duration-500 ${tunnelTransition ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100'}`}>
                        <StashButton large />
                    </div>

                    <button
                        ref={notificationButtonRef}
                        type="button"
                        aria-label="Open notifications"
                        aria-expanded={showNotificationFeed}
                        aria-controls="hub-notification-feed"
                        onClick={() => setShowNotificationFeed(current => !current)}
                        className={`fixed right-6 top-6 z-[220] flex h-12 w-12 items-center justify-center rounded-2xl border backdrop-blur-xl transition-all duration-300 ${
                            showNotificationFeed
                                ? 'border-blue-400/50 bg-blue-500/15 text-blue-300 shadow-[0_0_30px_rgba(59,130,246,0.2)]'
                                : 'border-slate-700/70 bg-slate-950/70 text-slate-400 hover:border-blue-500/40 hover:bg-slate-900/90 hover:text-white'
                        } ${tunnelTransition ? 'pointer-events-none scale-90 opacity-0' : 'opacity-100'}`}
                    >
                        <Bell size={20} strokeWidth={2.2} />
                        {unreadNotificationCount > 0 && (
                            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-slate-950 bg-red-500 px-1 text-[9px] font-black text-white shadow-lg">
                                {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                            </span>
                        )}
                    </button>

                    {showNotificationFeed && (
                        <div
                            id="hub-notification-feed"
                            ref={notificationPanelRef}
                            className="fixed right-6 top-20 z-[220] flex h-[440px] max-h-[calc(100vh-6.5rem)] w-[min(410px,calc(100vw-3rem))] flex-col overflow-hidden rounded-3xl border border-slate-700/60 bg-slate-950/95 shadow-[0_30px_90px_rgba(0,0,0,0.7)] backdrop-blur-2xl"
                        >
                            <div className="flex shrink-0 items-center justify-between border-b border-slate-800/80 px-5 py-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-blue-500/25 bg-blue-500/10 text-blue-400">
                                        <Bell size={17} />
                                    </div>
                                    <div>
                                        <h2 className="text-sm font-black uppercase tracking-[0.16em] text-white">Notifications</h2>
                                        <p className="mt-0.5 text-[10px] font-semibold text-slate-500">Your personal and team activity in one place</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    aria-label="Close notifications"
                                    onClick={() => setShowNotificationFeed(false)}
                                    className="rounded-xl p-2 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-400"
                                >
                                    <X size={17} />
                                </button>
                            </div>

                            <div className="grid shrink-0 grid-cols-2 border-b border-slate-800/80">
                                <button
                                    type="button"
                                    onClick={() => setNotificationFeedTab('team')}
                                    className={`relative flex items-center justify-center gap-2.5 px-4 py-3.5 transition-colors ${
                                        notificationFeedTab === 'team'
                                            ? 'bg-orange-500/[0.06] text-orange-300'
                                            : 'text-slate-500 hover:bg-slate-900/70 hover:text-slate-300'
                                    }`}
                                >
                                    <Users size={14} />
                                    <span className="text-[10px] font-black uppercase tracking-[0.18em]">Team</span>
                                    <span className={`rounded-full px-2 py-0.5 text-[8px] font-black ${
                                        unreadTeamNotificationCount > 0
                                            ? 'bg-orange-500/15 text-orange-300'
                                            : 'bg-slate-800 text-slate-500'
                                    }`}>
                                        {unreadTeamNotificationCount} new
                                    </span>
                                    {notificationFeedTab === 'team' && (
                                        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-orange-400" />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setNotificationFeedTab('personal')}
                                    className={`relative flex items-center justify-center gap-2.5 border-l border-slate-800/80 px-4 py-3.5 transition-colors ${
                                        notificationFeedTab === 'personal'
                                            ? 'bg-blue-500/[0.06] text-blue-300'
                                            : 'text-slate-500 hover:bg-slate-900/70 hover:text-slate-300'
                                    }`}
                                >
                                    <User size={14} />
                                    <span className="text-[10px] font-black uppercase tracking-[0.18em]">Personal</span>
                                    <span className={`rounded-full px-2 py-0.5 text-[8px] font-black ${
                                        unreadPersonalNotificationCount > 0
                                            ? 'bg-blue-500/15 text-blue-300'
                                            : 'bg-slate-800 text-slate-500'
                                    }`}>
                                        {unreadPersonalNotificationCount} new
                                    </span>
                                    {notificationFeedTab === 'personal' && (
                                        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-blue-400" />
                                    )}
                                </button>
                            </div>

                            {notificationFeedTab === 'team' ? (
                                <section className="flex min-h-0 flex-1 flex-col">
                                    {unreadTeamNotificationCount > 0 && (
                                        <div className="flex shrink-0 items-center justify-end border-b border-slate-800/40 px-5 py-2">
                                            <button
                                                type="button"
                                                onClick={markAllTeamNotificationsRead}
                                                className="flex items-center gap-1 text-[8px] font-black uppercase tracking-wider text-slate-500 transition-colors hover:text-orange-300"
                                            >
                                                <CheckCheck size={12} />
                                                Read all
                                            </button>
                                        </div>
                                    )}

                                    <div className="min-h-0 flex-1 overflow-y-auto">
                                        {teamNotifications.length === 0 ? (
                                            <div className="flex h-full flex-col items-center justify-center px-8 text-center">
                                                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-orange-500/15 bg-orange-500/[0.06] text-orange-400/70">
                                                    <Users size={20} />
                                                </div>
                                                <p className="text-xs font-black uppercase tracking-wider text-slate-300">All caught up</p>
                                                <p className="mt-2 text-[10px] font-medium text-slate-600">Your Team Space notifications will appear here.</p>
                                            </div>
                                        ) : (
                                            teamNotifications.map(notification => (
                                                <button
                                                    key={notification.id}
                                                    type="button"
                                                    onClick={() => openTeamNotification(notification)}
                                                    className={`group flex w-full gap-3 border-b border-slate-800/50 px-5 py-4 text-left transition-colors hover:bg-slate-800/45 ${
                                                        notification.read ? 'bg-transparent' : 'bg-orange-500/[0.045]'
                                                    }`}
                                                >
                                                    <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
                                                        notification.read ? 'bg-slate-700' : 'bg-orange-400 shadow-[0_0_10px_rgba(251,146,60,0.65)]'
                                                    }`} />
                                                    <span className="min-w-0 flex-1">
                                                        <span className="flex items-start justify-between gap-3">
                                                            <span className={`truncate text-[11px] font-bold ${notification.read ? 'text-slate-400' : 'text-slate-100'}`}>
                                                                {notification.title}
                                                            </span>
                                                            <span className="shrink-0 text-[8px] font-bold uppercase tracking-wider text-slate-600">
                                                                {formatNotificationAge(notification.createdAt)}
                                                            </span>
                                                        </span>
                                                        <span className="mt-1 line-clamp-2 block text-[10px] font-medium leading-relaxed text-slate-500">
                                                            {notification.body}
                                                        </span>
                                                        <span className="mt-2 block text-[8px] font-black uppercase tracking-[0.16em] text-orange-400/60">
                                                            {getNotificationTeamName(notification.teamId)}
                                                        </span>
                                                    </span>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </section>
                            ) : (
                                <section className="flex min-h-0 flex-1 flex-col">
                                    {unreadPersonalNotificationCount > 0 && (
                                        <div className="flex shrink-0 items-center justify-end border-b border-slate-800/40 px-5 py-2">
                                            <button
                                                type="button"
                                                onClick={markAllPersonalNotificationsRead}
                                                className="flex items-center gap-1 text-[8px] font-black uppercase tracking-wider text-slate-500 transition-colors hover:text-blue-300"
                                            >
                                                <CheckCheck size={12} />
                                                Read all
                                            </button>
                                        </div>
                                    )}
                                    <div className="min-h-0 flex-1 overflow-y-auto">
                                        {personalNotifications.length === 0 ? (
                                            <div className="flex h-full flex-col items-center justify-center px-8 text-center">
                                                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-blue-500/15 bg-blue-500/[0.06] text-blue-400/70">
                                                    <Bell size={20} />
                                                </div>
                                                <p className="text-xs font-black uppercase tracking-wider text-slate-300">All caught up</p>
                                                <p className="mt-2 max-w-[250px] text-[10px] font-medium leading-relaxed text-slate-600">
                                                    Forum replies, weekly rewards, trophies and new badges will appear here.
                                                </p>
                                            </div>
                                        ) : (
                                            personalNotifications.map(notification => (
                                                <button
                                                    key={notification.id}
                                                    type="button"
                                                    onClick={() => openPersonalNotification(notification)}
                                                    className={`group flex w-full gap-3 border-b border-slate-800/50 px-5 py-4 text-left transition-colors hover:bg-slate-800/45 ${
                                                        notification.read ? 'bg-transparent' : 'bg-blue-500/[0.05]'
                                                    }`}
                                                >
                                                    <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border ${
                                                        notification.read
                                                            ? 'border-slate-800 bg-slate-900/60 text-slate-600'
                                                            : 'border-blue-500/20 bg-blue-500/10 text-blue-300'
                                                    }`}>
                                                        {personalNotificationIcon(notification.type)}
                                                    </span>
                                                    <span className="min-w-0 flex-1">
                                                        <span className="flex items-start justify-between gap-3">
                                                            <span className={`text-[11px] font-bold ${notification.read ? 'text-slate-400' : 'text-slate-100'}`}>
                                                                {notification.title}
                                                            </span>
                                                            <span className="shrink-0 text-[8px] font-bold uppercase tracking-wider text-slate-600">
                                                                {formatNotificationAge(notification.createdAt)}
                                                            </span>
                                                        </span>
                                                        <span className="mt-1 line-clamp-4 block text-[10px] font-medium leading-relaxed text-slate-500">
                                                            {notification.body}
                                                        </span>
                                                    </span>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </section>
                            )}
                        </div>
                    )}
                </>
            )}
            <div className="hub-main-surface min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 relative overflow-hidden"
                style={{
                    transform: tunnelTransition ? 'scale(0.4)' : (menuFadeIn ? (menuFadeReady ? 'scale(1)' : 'scale(0.7)') : 'scale(1)'),
                    filter: tunnelTransition ? 'blur(12px) brightness(0.2)' : (menuFadeIn ? (menuFadeReady ? 'none' : 'blur(8px) brightness(0.3)') : 'none'),
                    transition: tunnelTransition
                        ? 'transform 1.4s cubic-bezier(0.4, 0, 0.2, 1), filter 1.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        : 'transform 2s cubic-bezier(0.25, 0.8, 0.25, 1), filter 1.8s cubic-bezier(0.25, 0.8, 0.25, 1)',
                    transformOrigin: 'center center',
                }}
            >
                {/* Welcome Wizard Overlay */}
                {showWizard && (
                    <WelcomeWizard
                        faceitTeams={faceitTeams}
                        loadingFaceitTeams={loadingFaceitTeams}
                        noFaceitAccount={noFaceitAccount}
                        user={user}
                        creatingTeamId={creatingTeamId}
                        teamActionError={error}
                        onTeamAction={async (teamId, action, team, serverLocation) => {
                            setCreatingTeamId(teamId);
                            setError('');
                            try {
                                const res = await axios.post(`${API}/teams${action === 'join' ? '/join-faceit' : ''}`, 
                                    action === 'join' ? { faceitId: teamId } : { faceitUrl: `https://www.faceit.com/en/teams/${teamId}`, serverLocation: serverLocation || 'europe-west' }
                                );
                                await refreshUser();
                                setShowWizard(false);
                                const token = localStorage.getItem('token');
                                axios.post(`${API}/auth/onboarding-done`, {}, {
                                    headers: { Authorization: `Bearer ${token}` },
                                }).catch(() => {});
                                if (res.data?.id || res.data?.teamId) {
                                    // For join: res.data is a TeamMember (id=membershipId, teamId=teamId)
                                    // For create: res.data is a Team (id=teamId)
                                    const targetTeamId = action === 'join' ? (res.data.teamId || res.data.id) : (res.data.id || res.data.teamId);
                                    handleSwitchTeam(targetTeamId);
                                }
                            } catch (err: any) {
                                setError(err.response?.data?.message || `Failed to ${action} team`);
                            }
                            setCreatingTeamId(null);
                        }}
                        onComplete={() => {
                            setShowWizard(false);
                            const token = localStorage.getItem('token');
                            axios.post(`${API}/auth/onboarding-done`, {}, {
                                headers: { Authorization: `Bearer ${token}` },
                            }).catch(() => {});
                        }}
                    />
                )}
                <style>{`
                @keyframes premium-shimmer { 0% { left: -100%; } 100% { left: 200%; } }
                .premium-btn { position: relative; overflow: hidden; background: linear-gradient(135deg, #7c3aed, #9333ea, #a855f7); border: 1px solid rgba(168, 85, 247, 0.5); color: white; }
                .premium-btn::after { content: ''; position: absolute; top: 0; left: -100%; width: 60%; height: 100%; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent); animation: premium-shimmer 2.5s ease-in-out infinite; }
                .hub-card { transition: transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1), border-color 0.3s ease, box-shadow 0.4s ease; }
                .hub-card:hover { transform: translateY(-4px); }
                .hub-card.expanded { flex: 1 1 100%; }
                `}</style>

                <div className="bg-grid"></div>

                <video ref={heroVideoRef} muted playsInline className="fixed inset-0 pointer-events-none" style={{ width: '100vw', height: '100vh', objectFit: 'cover', opacity: 0.15, zIndex: 0 }} ></video>
                <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 55% 60% at 50% 50%, rgba(2,6,23,0.95) 0%, rgba(2,6,23,0.8) 40%, rgba(2,6,23,0.4) 70%, transparent 100%)', zIndex: 1 }} />

                <div className="w-full max-w-4xl relative z-10">
                    {/* ═══ Logo ═══ */}
                    <div className="text-center mb-10">
                        <h1 className="relative mb-2 flex justify-center" aria-label={t('login.title')}>
                            <img
                                src="/images/stratsync-wordmark.svg"
                                alt=""
                                aria-hidden="true"
                                className="block h-auto w-[min(440px,82vw)] select-none drop-shadow-[0_0_18px_rgba(123,140,255,0.2)]"
                            />
                        </h1>
                        <p className="relative text-[14px] text-[#7b8cff] font-bold tracking-[3px] uppercase">{t('login.subtitle')}</p>
                    </div>

                    {/* ═══ Main Cards ═══ */}
                    {!isCheckingWizard && (
                        <div className="hub-space-cards flex gap-6 mb-8" style={{ minHeight: hasTeams ? 380 : 340 }}>

                        {/* ──── Team Space Card (orange, left) ──── */}
                        {hasTeams && (() => {
                            const sorted = [...memberships].sort((a, b) => {
                                // The API stores the last entered Teamspace in user.teamId.
                                // Keep that team at the first carousel position on every hub visit.
                                if (a.team.id === user?.teamId) return -1;
                                if (b.team.id === user?.teamId) return 1;
                                const ia = teamOrder.indexOf(a.team.id);
                                const ib = teamOrder.indexOf(b.team.id);
                                if (ia === -1 && ib === -1) return 0;
                                if (ia === -1) return 1;
                                if (ib === -1) return -1;
                                return ia - ib;
                            });
                            const safeIndex = Math.min(currentTeamIndex, sorted.length - 1);
                            const currentMembership = sorted[safeIndex];
                            if (!currentMembership) return null;
                            const detail = teamDetails[currentMembership.team.id];
                            const memberCount = detail?.members?.length || 0;
                            const hasMultipleTeams = sorted.length > 1;
                            return (
                                <div
                                    className={cn(
                                        "hub-card flex-1 bg-slate-900/60 backdrop-blur-sm border border-orange-500/25 rounded-3xl p-7 flex flex-col relative cursor-pointer group shadow-2xl"
                                    )}
                                    onClick={() => handleSwitchTeam(currentMembership.team.id)}
                                    style={{ boxShadow: '0 0 40px rgba(234, 88, 12, 0.06), 0 25px 50px -12px rgba(0,0,0,0.5)' }}
                                >
                                    {/* Top-corner team navigation arrows */}
                                    {hasMultipleTeams && (
                                        <>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setCurrentTeamIndex(prev => (prev - 1 + sorted.length) % sorted.length); }}
                                                className="absolute top-4 left-4 z-10 p-2 rounded-xl text-slate-500 hover:text-orange-400 hover:bg-orange-500/10 border border-transparent hover:border-orange-500/20 transition-all"
                                            >
                                                <ChevronLeft size={20} strokeWidth={2.5} />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setCurrentTeamIndex(prev => (prev + 1) % sorted.length); }}
                                                className="absolute top-4 right-4 z-10 p-2 rounded-xl text-slate-500 hover:text-orange-400 hover:bg-orange-500/10 border border-transparent hover:border-orange-500/20 transition-all"
                                            >
                                                <ChevronRight size={20} strokeWidth={2.5} />
                                            </button>
                                        </>
                                    )}

                                    {/* Label */}
                                    <div className="flex items-center justify-center gap-2 mb-5">
                                        <LayoutDashboard size={14} className="text-orange-500" />
                                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-400/70">Team Space</span>
                                    </div>

                                    {/* Team info — centered */}
                                    <div className="relative flex items-center justify-center gap-4 mb-5">
                                        {/* Leave team — same rules as the Team tab: owners must hand over
                                            ownership (or delete the team) before they can leave. */}
                                        {(() => {
                                            const ownerId = currentMembership.team.ownerId;
                                            const isTeamOwner = !!ownerId && ownerId === user?.id;
                                            const isUnclaimed = !!ownerId && ownerId.startsWith('UNCLAIMED_');
                                            const willDelete = (detail?.members?.length ?? 0) <= 1 && isUnclaimed;
                                            return (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (isTeamOwner) return;
                                                        setIconTooltip(null);
                                                        setTeamToLeave({ ...currentMembership.team, willDelete });
                                                    }}
                                                    onMouseMove={(e) => setIconTooltip({
                                                        x: e.clientX,
                                                        y: e.clientY,
                                                        label: isTeamOwner ? 'Transfer ownership first' : 'Leave team',
                                                        color: isTeamOwner ? 'text-slate-400' : 'text-red-400',
                                                    })}
                                                    onMouseLeave={() => setIconTooltip(null)}
                                                    className={cn(
                                                        "absolute left-0 top-1/2 -translate-y-1/2 p-2 rounded-xl border transition-all",
                                                        isTeamOwner
                                                            ? "text-slate-600 bg-slate-800/40 border-slate-700/40 cursor-not-allowed"
                                                            : "text-red-400/70 bg-red-500/5 border-red-500/20 hover:text-red-300 hover:bg-red-500/15 hover:border-red-400/40 active:scale-90"
                                                    )}
                                                >
                                                    <LogOut size={16} />
                                                </button>
                                            );
                                        })()}
                                        {/* Team logo */}
                                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center border border-orange-500/20 overflow-hidden bg-slate-900 bg-cover bg-center flex-shrink-0"
                                            style={currentMembership.team.logoUrl ? { backgroundImage: `url(${currentMembership.team.logoUrl})` } : {}}>
                                            {!currentMembership.team.logoUrl && <span className="text-2xl font-black text-orange-400/60 uppercase">{currentMembership.team.name.charAt(0)}</span>}
                                        </div>
                                        <div className="min-w-0">
                                            <h2 className="text-xl font-black text-white italic uppercase truncate leading-tight">{currentMembership.team.name}</h2>
                                            <div className="flex items-center gap-3 mt-1">
                                                <span className="text-[9px] font-black text-orange-400/60 uppercase tracking-widest flex items-center gap-1">
                                                    {currentMembership.role === 'Manager' && <Shield size={9} className="text-amber-500 fill-amber-500/20" />}
                                                    {currentMembership.role}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Member avatars + count */}
                                    {detail && detail.members.length > 0 && (
                                        <div className="flex items-center justify-center gap-3 mb-5">
                                            <div className="flex items-center -space-x-2">
                                                {detail.members.slice(0, 6).map((mem) => (
                                                    <div key={mem.id} className="w-8 h-8 rounded-full border-2 border-slate-900 bg-slate-800 flex items-center justify-center overflow-hidden bg-cover bg-center"
                                                        style={mem.avatarUrl ? { backgroundImage: `url(${mem.avatarUrl})` } : {}}
                                                        title={mem.ingameName || mem.username}>
                                                        {!mem.avatarUrl && <span className="text-[8px] font-black text-slate-500 uppercase">{(mem.ingameName || mem.username).charAt(0)}</span>}
                                                    </div>
                                                ))}
                                                {detail.members.length > 6 && (
                                                    <div className="w-8 h-8 rounded-full border-2 border-slate-900 bg-slate-800 flex items-center justify-center">
                                                        <span className="text-[8px] font-black text-slate-500">+{detail.members.length - 6}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1">
                                                <Users size={10} /> {memberCount}
                                            </span>
                                        </div>
                                    )}

                                    {/* League tile */}
                                    {detail?.league && (
                                        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3 mb-4 text-center">
                                            <div className="flex items-center justify-center gap-2 mb-2">
                                                <Trophy size={12} className="text-amber-400" />
                                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{detail.league.name}</span>
                                            </div>
                                            <div className="flex items-center justify-center gap-4">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-lg font-black text-emerald-400">{detail.league.wins}</span>
                                                    <span className="text-[8px] font-bold text-slate-500 uppercase">W</span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-lg font-black text-slate-400">{detail.league.draws}</span>
                                                    <span className="text-[8px] font-bold text-slate-500 uppercase">D</span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-lg font-black text-red-400">{detail.league.losses}</span>
                                                    <span className="text-[8px] font-bold text-slate-500 uppercase">L</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {!detail?.league && (
                                        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3 mb-4 text-center">
                                            <div className="flex items-center justify-center gap-2 mb-1">
                                                <Trophy size={12} className="text-slate-500" />
                                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">No Active League</span>
                                            </div>
                                            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">
                                                {(detail?.completedLeagues ?? 0) > 0
                                                    ? `${detail!.completedLeagues} completed league${detail!.completedLeagues === 1 ? '' : 's'}`
                                                    : 'No leagues yet'}
                                            </p>
                                        </div>
                                    )}

                                    {/* Enter button */}
                                    <button
                                        disabled={isSwitching}
                                        className="w-full mt-auto bg-orange-600/15 border border-orange-500/30 text-orange-400 font-black py-3.5 rounded-xl transition-all uppercase tracking-widest text-xs active:scale-95 shadow-lg hover:bg-orange-600/25 hover:border-orange-500/50 hover:shadow-orange-500/10 disabled:opacity-50 group-hover:bg-orange-600/20"
                                    >
                                        <ChevronRight size={14} className="inline mr-1.5 -mt-0.5" />
                                        Enter Team Space
                                    </button>
                                </div>
                            );
                        })()}

                        {/* ──── Personal Space Card (blue, right — always visible) ──── */}
                        <div
                            className={cn(
                                "hub-card bg-slate-900/60 backdrop-blur-sm border border-blue-500/25 rounded-3xl p-7 flex flex-col relative cursor-pointer group shadow-2xl",
                                hasTeams ? 'flex-1' : 'flex-1 max-w-xl mx-auto'
                            )}
                            onClick={handlePersonalSpace}
                            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.06), 0 25px 50px -12px rgba(0,0,0,0.5)' }}
                        >
                            {/* Label */}
                            <div className="flex items-center justify-center gap-2 mb-5">
                                <User size={14} className="text-blue-400" />
                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-400/70">Personal Space</span>
                            </div>

                            {/* Player info — centered, avatar then text */}
                            <div className="flex items-center justify-center gap-4 mb-5">
                                <div className="w-16 h-16 rounded-2xl flex items-center justify-center border border-blue-500/20 overflow-hidden bg-slate-900 bg-cover bg-center flex-shrink-0"
                                    style={user?.avatarUrl ? { backgroundImage: `url(${user.avatarUrl})` } : {}}>
                                    {!user?.avatarUrl && <User size={28} className="text-blue-400" />}
                                </div>
                                <div className="min-w-0">
                                    <h2 className="text-xl font-black text-white italic uppercase truncate leading-tight">{user?.ingameName || user?.username || 'Player'}</h2>
                                    {user?.faceitElo && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            <img src="/icons/socials/Icon_Faceit.png" alt="Faceit" className="w-3.5 h-3.5" />
                                            <span className="text-[11px] font-black text-amber-400/80 uppercase tracking-widest">
                                                {user.faceitElo} ELO
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Features hint — centered, wraps so the full mode list always fits */}
                            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[10px] font-bold text-slate-500 mb-5">
                                <span className="flex items-center gap-1"><Gamepad2 size={10} /> Game Mods</span>
                                <span className="flex items-center gap-1"><Target size={10} /> Lineups</span>
                                <span className="flex items-center gap-1"><Film size={10} /> Demos</span>
                                <span className="flex items-center gap-1"><Play size={10} /> Replays</span>
                                <span className="flex items-center gap-1"><Crosshair size={10} /> Ranked</span>
                            </div>

                            {/* Deathmatch tiles — live rating + current placement */}
                            <div className="grid grid-cols-2 gap-3 mb-5">
                                <div className="bg-slate-800/60 border border-blue-500/15 rounded-xl p-3.5 text-center">
                                    <div className="flex items-center justify-center gap-1.5 mb-1.5">
                                        <Server size={11} className="text-emerald-400" />
                                        <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Deathmatch</span>
                                    </div>
                                    <span className="text-2xl font-black text-emerald-400">{dmStats ? dmStats.elo.toLocaleString() : '—'}</span>
                                    <p className="text-[8px] font-bold text-slate-600 uppercase tracking-wider mt-0.5">ELO</p>
                                </div>
                                <div className="bg-slate-800/60 border border-blue-500/15 rounded-xl p-3.5 text-center">
                                    <div className="flex items-center justify-center gap-1.5 mb-1.5">
                                        <Trophy size={11} className="text-amber-400" />
                                        <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Placement</span>
                                    </div>
                                    <span className="text-2xl font-black text-amber-400">{dmStats?.rank ? `#${dmStats.rank}` : '—'}</span>
                                    <p className="text-[8px] font-bold text-slate-600 uppercase tracking-wider mt-0.5">{dmStats?.rank ? 'Rank' : 'Unranked'}</p>
                                </div>
                            </div>

                            {/* Enter button */}
                            <button
                                className="w-full mt-auto bg-blue-600/15 border border-blue-500/30 text-blue-400 font-black py-3.5 rounded-xl transition-all uppercase tracking-widest text-xs active:scale-95 shadow-lg hover:bg-blue-600/25 hover:border-blue-500/50 hover:shadow-blue-500/10 group-hover:bg-blue-600/20"
                            >
                                <ChevronRight size={14} className="inline mr-1.5 -mt-0.5" />
                                Enter Personal Space
                            </button>
                        </div>
                    </div>
                    )}

                    {/* ═══ Join / Create Row ═══ */}
                    {showButtons && !activePanel && !isCheckingWizard && (
                        <div className="flex flex-col items-center justify-center gap-4 mb-10">
                            <button
                                onClick={() => needsPremiumForMore ? setShowPremiumGate(true) : expandPanel('add')}
                                className={`menu-add-team${user && memberships.length === 0 ? ' menu-add-team--empty' : ''}`}
                            >
                                <Plus size={16} />
                                Add Team
                            </button>
                            <button
                                onClick={() => navigate('/forum')}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-700/60 bg-slate-900/40 text-slate-400 hover:text-white hover:bg-slate-800/80 hover:border-slate-500 transition-all active:scale-95 shadow-lg group backdrop-blur-sm"
                            >
                                <Search size={14} className="text-blue-400 group-hover:scale-110 transition-transform duration-300" />
                                <span className="text-[10px] font-black uppercase tracking-[0.2em]">Find Team</span>
                            </button>
                        </div>
                    )}

                    {/* ═══ Max Teams Reached (premium users at 3 teams) ═══ */}
                    {isMaxTeams && !activePanel && !isCheckingWizard && (
                        <div className="flex items-center justify-center mb-10">
                            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl border border-slate-700/30 bg-slate-800/30 text-slate-500 text-xs font-bold uppercase tracking-widest">
                                <Shield size={14} />
                                Maximum number of teams reached (3/3)
                            </div>
                        </div>
                    )}

                    {/* ═══ Premium Upsell Overlay ═══ */}
                    {showPremiumGate && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setShowPremiumGate(false)}>
                            <div className="w-full max-w-sm bg-slate-900/95 backdrop-blur-xl border border-purple-500/30 rounded-3xl p-8 shadow-2xl animate-in fade-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                                <div className="flex justify-end mb-2">
                                    <button onClick={() => setShowPremiumGate(false)} className="p-2 rounded-lg text-red-500 hover:text-red-400 hover:bg-red-500/10 transition-all">
                                        <X size={18} />
                                    </button>
                                </div>
                                <div className="flex flex-col items-center text-center gap-5">
                                    <div className="w-16 h-16 rounded-2xl bg-purple-500/10 flex items-center justify-center">
                                        <Sparkles size={32} className="text-purple-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-black text-white mb-2">Premium Required</h3>
                                        <p className="text-sm text-slate-400 leading-relaxed">
                                            To join or create more than one team, you need a <span className="text-purple-400 font-bold">Premium</span> subscription.
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => { setShowPremiumGate(false); navigate('/premium'); }}
                                        className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-purple-600/20 border border-purple-500/30 text-purple-300 font-black text-sm uppercase tracking-widest hover:bg-purple-600/30 hover:border-purple-500/50 transition-all"
                                    >
                                        <Sparkles size={16} />
                                        Get Premium
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}



                    {/* ═══ Expanded Create Panel — Faceit Team Picker ═══ */}
                    {activePanel === 'add' && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in duration-200" onClick={collapsePanel}>
                            <div className="w-full max-w-xl bg-slate-900/95 backdrop-blur-xl border border-blue-500/30 rounded-3xl p-8 shadow-2xl animate-in fade-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-blue-500/10 text-blue-500 rounded-xl flex items-center justify-center">
                                            <Users size={24} strokeWidth={2.5} />
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-black text-white italic uppercase">Add Team</h2>
                                            <p className="text-xs text-slate-500 font-semibold">Select a Faceit team to join or create</p>
                                        </div>
                                    </div>
                                    <button onClick={collapsePanel} className="p-2 rounded-lg text-red-500 hover:text-red-400 hover:bg-red-500/10 transition-all">
                                        <X size={18} />
                                    </button>
                                </div>

                                {loadingFaceitTeams ? (
                                    <div className="flex flex-col items-center justify-center py-12 gap-3">
                                        <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                                        <p className="text-slate-500 text-sm font-semibold">Loading your Faceit teams...</p>
                                    </div>
                                ) : noFaceitAccount ? (
                                    <div className="flex flex-col items-center justify-center py-10 gap-4">
                                        <div className="w-16 h-16 rounded-2xl bg-orange-500/10 flex items-center justify-center">
                                            <AlertTriangle size={32} className="text-orange-500" />
                                        </div>
                                        <div className="text-center">
                                            <p className="text-white font-bold text-sm mb-1">No Faceit Account Linked</p>
                                            <p className="text-slate-500 text-xs leading-relaxed max-w-xs">
                                                Your account is not linked to a Faceit profile. Log in with Faceit or link your Steam account to a Faceit profile to create a team.
                                            </p>
                                        </div>
                                    </div>
                                ) : faceitTeams.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-10 gap-4">
                                        <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center">
                                            <Users size={32} className="text-slate-600" />
                                        </div>
                                        <div className="text-center">
                                            <p className="text-white font-bold text-sm mb-1">No Faceit Teams Found</p>
                                            <p className="text-slate-500 text-xs leading-relaxed max-w-xs">
                                                You are not a member of any Faceit team. Join or create a team on{' '}
                                                <a href="https://www.faceit.com" target="_blank" rel="noreferrer" className="text-orange-400 hover:text-orange-300 underline">faceit.com</a>{' '}
                                                first, then come back to register it here.
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                                        {[...faceitTeams].sort((a: any, b: any) => {
                                            const aJoinable = a.alreadyExists && !a.alreadyJoined;
                                            const bJoinable = b.alreadyExists && !b.alreadyJoined;
                                            const aCreatable = !a.alreadyExists && a.isOwner;
                                            const bCreatable = !b.alreadyExists && b.isOwner;
                                            const rank = (creatable: boolean, joinable: boolean) => {
                                                if (joinable) return 1;
                                                if (creatable) return 2;
                                                return 3;
                                            };
                                            return rank(aCreatable, aJoinable) - rank(bCreatable, bJoinable);
                                        }).map((team: any) => {
                                            const isActualOwner = team.isOwner === true;
                                            const joinable = team.alreadyExists && !team.alreadyJoined;
                                            const creatable = !team.alreadyExists && isActualOwner;
                                            const isMember = team.alreadyExists && team.alreadyJoined;

                                            return (
                                                <div key={team.teamId} className={cn(
                                                    "w-full flex flex-wrap items-center justify-between gap-4 p-4 rounded-xl border transition-all",
                                                    isMember ? "border-emerald-500/25 bg-emerald-500/5" :
                                                    joinable ? "border-slate-700 bg-slate-800/40" :
                                                    "border-slate-800 bg-slate-950/50 hover:border-blue-500/30"
                                                )}>
                                                    <div className="flex items-center gap-4 min-w-0 flex-1">
                                                        {team.avatar ? (
                                                            <img src={team.avatar} alt="" className={cn("shrink-0 w-12 h-12 rounded-xl object-cover border transition-all", team.alreadyExists ? "border-slate-700" : "border-slate-800")} />
                                                        ) : (
                                                            <div className={cn("shrink-0 w-12 h-12 rounded-xl flex items-center justify-center font-black text-lg border", team.alreadyExists ? "bg-slate-800 text-slate-500 border-slate-700" : "bg-slate-800 text-slate-500 border-slate-700")}>
                                                                {team.name[0]}
                                                            </div>
                                                        )}
                                                        <div className="flex-1 text-left min-w-0">
                                                            <p className={"font-bold text-sm truncate text-white"}>{team.name}</p>
                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                <p className="text-slate-500 text-xs font-semibold">{team.membersCount > 0 ? `${team.membersCount} members` : 'Faceit Team'}</p>
                                                                {joinable && (
                                                                    <p className="text-orange-400/90 text-xs italic font-semibold truncate">- Existing Team</p>
                                                                )}
                                                                {!team.alreadyExists && !isActualOwner && (
                                                                    <p className="text-slate-400 text-[10px] uppercase font-bold tracking-wider opacity-60 ml-1">Member</p>
                                                                )}
                                                                {creatable && isActualOwner && (
                                                                    <p className="text-amber-500 text-[10px] uppercase font-bold tracking-wider opacity-80 ml-1">Owner</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Action Buttons */}
                                                    <div className="flex items-center shrink-0">
                                                        {creatable && (
                                                            <button
                                                                onClick={async () => {
                                                                    setCreatingTeamId(team.teamId);
                                                                    setError('');
                                                                    try {
                                                                        const currentTeamCount = user?.teamMemberships?.length || 0;
                                                                        if (currentTeamCount >= 1 && !user?.isPremium) {
                                                                            setError('You already have a team. Premium is required to manage multiple teams simultaneously.');
                                                                            setCreatingTeamId(null);
                                                                            return;
                                                                        }
                                                                        const res = await axios.post(`${API}/teams`, {
                                                                            faceitUrl: `https://www.faceit.com/en/teams/${team.teamId}`
                                                                        });
                                                                        await refreshUser();
                                                                        collapsePanel();
                                                                        if (res.data?.id) {
                                                                            // Navigate directly — create already sets the user's active team
                                                                            setTunnelTransition(true);
                                                                            sessionStorage.setItem('dashboardFadeIn', 'true');
                                                                            sessionStorage.setItem('tunnelOverlayActive', 'true');
                                                                            setTimeout(() => {
                                                                                navigate(`/team/${res.data.id}/dashboard`);
                                                                            }, 1500);
                                                                        }
                                                                    } catch (err: any) {
                                                                        setError(err.response?.data?.message || 'Failed to create team');
                                                                    }
                                                                    setCreatingTeamId(null);
                                                                }}
                                                                disabled={!!creatingTeamId}
                                                                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs uppercase tracking-wider rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/20 active:scale-95 flex items-center justify-center min-w-[100px]"
                                                            >
                                                                {creatingTeamId === team.teamId ? (
                                                                    <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                                ) : "Create"}
                                                            </button>
                                                        )}
                                                        {joinable && (
                                                            <button
                                                                onClick={async () => {
                                                                    setCreatingTeamId(team.teamId);
                                                                    setError('');
                                                                    try {
                                                                        const currentTeamCount = (user?.teamMemberships || []).filter(m => (m as any).leftReason !== 'PENDING_INVITE').length;
                                                                        if (currentTeamCount >= 1 && !user?.isPremium) {
                                                                            setError('Premium is required to join multiple teams.');
                                                                            setCreatingTeamId(null);
                                                                            return;
                                                                        }
                                                                        const res = await axios.post(`${API}/teams/join-faceit`, {
                                                                            faceitId: team.teamId
                                                                        });
                                                                        await refreshUser();
                                                                        collapsePanel();
                                                                        if (res.data?.teamId) {
                                                                            // Navigate directly — join already sets the user's active team
                                                                            setTunnelTransition(true);
                                                                            sessionStorage.setItem('dashboardFadeIn', 'true');
                                                                            sessionStorage.setItem('tunnelOverlayActive', 'true');
                                                                            setTimeout(() => {
                                                                                navigate(`/team/${res.data.teamId}/dashboard`);
                                                                            }, 1500);
                                                                        }
                                                                    } catch (err: any) {
                                                                        setError(err.response?.data?.message || 'Failed to join team');
                                                                    }
                                                                    setCreatingTeamId(null);
                                                                }}
                                                                disabled={!!creatingTeamId}
                                                                className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white font-bold text-xs uppercase tracking-wider rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-500/20 active:scale-95 flex items-center justify-center min-w-[100px]"
                                                            >
                                                                {creatingTeamId === team.teamId ? (
                                                                    <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                                ) : "Join Team"}
                                                            </button>
                                                        )}
                                                        {isMember && <span className="text-emerald-400 text-xs font-bold uppercase">Already joined</span>}
                                                    </div>
                                                    {!team.alreadyExists && !isActualOwner && (
                                                        <p className="text-slate-400 text-xs w-full">Only the owner of this Faceit team can create it in StratSync.</p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {error && (
                                    <div className="mt-4 bg-red-500/10 border border-red-500/50 text-red-500 p-3 rounded-xl text-center font-bold flex items-center justify-center gap-2 text-sm">
                                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" /> {error}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}


                    {/* ═══ Bottom Icons ═══ */}
                    <div className="text-center flex flex-col items-center" style={{
                        transition: activePanel
                            ? 'transform 0.6s cubic-bezier(0.25, 0.8, 0.25, 1), opacity 0.4s'
                            : 'transform 1s cubic-bezier(0.25, 0.8, 0.25, 1) 0.5s, opacity 0.8s 0.5s',
                        transform: activePanel ? 'translateY(40px)' : 'translateY(0)',
                        opacity: activePanel ? 0 : 1,
                        pointerEvents: activePanel ? 'none' : 'auto',
                    }}>
                        <div className="flex items-center gap-3" onMouseLeave={() => setIconTooltip(null)}>
                            <button
                                onClick={() => navigate('/settings')}
                                className="p-3 bg-slate-800/80 border border-slate-700 rounded-xl text-slate-400 hover:text-white hover:border-slate-500 hover:bg-slate-700/80 transition-all active:scale-90 shadow-lg backdrop-blur-sm group"
                                onMouseMove={(e) => setIconTooltip({ x: e.clientX, y: e.clientY, label: t('settings.title'), color: 'text-slate-300' })}
                                onMouseLeave={() => setIconTooltip(null)}
                            >
                                <Settings size={28} className="group-hover:rotate-90 transition-transform duration-300" />
                            </button>
                            <button
                                onClick={logout}
                                className="p-3 bg-red-900/40 border border-red-500/30 rounded-xl text-red-400/70 hover:text-red-300 hover:bg-red-900/60 hover:border-red-400/50 transition-all active:scale-90 shadow-lg backdrop-blur-sm group"
                                onMouseMove={(e) => setIconTooltip({ x: e.clientX, y: e.clientY, label: t('common.logout'), color: 'text-red-400' })}
                                onMouseLeave={() => setIconTooltip(null)}
                            >
                                <LogOut size={28} className="group-hover:scale-110 transition-transform duration-300" />
                            </button>
                            <button
                                onClick={() => navigate('/premium')}
                                className="p-3 border border-purple-500/30 rounded-xl text-purple-400/70 hover:text-purple-300 hover:border-purple-400/50 transition-all active:scale-90 shadow-lg backdrop-blur-sm group"
                                style={{ background: 'rgba(88, 28, 135, 0.3)' }}
                                onMouseMove={(e) => setIconTooltip({ x: e.clientX, y: e.clientY, label: 'Premium', color: 'text-purple-400' })}
                                onMouseLeave={() => setIconTooltip(null)}
                            >
                                <Crown size={28} className="group-hover:scale-110 transition-transform duration-300" />
                            </button>
                        </div>
                        {/* Browser Extension Badge */}
                        {!showWizard && (
                            <button
                                onClick={() => navigate('/browser-extension')}
                                className={`hub-menu-extension mt-6 flex items-center gap-2 px-3 py-2.5 rounded-xl border border-blue-500/20 bg-slate-900/80 backdrop-blur-sm text-blue-400 hover:text-blue-300 hover:bg-slate-800/90 hover:border-blue-400/40 transition-all duration-500 active:scale-95 shadow-lg group ${tunnelTransition ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100'}`}
                                style={{ boxShadow: '0 0 15px rgba(59,130,246,0.1), 0 4px 12px rgba(0,0,0,0.3)' }}
                            >
                                <Chrome size={16} className="group-hover:scale-110 transition-transform duration-300" />
                                <span className="text-[9px] font-bold uppercase tracking-[0.12em] opacity-80 group-hover:opacity-100">Browser Extension</span>
                            </button>
                        )}

                    </div>
                </div>

                {/* Leave Team Confirmation Modal */}
                {teamToLeave && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl p-8 shadow-2xl animate-in zoom-in-95 duration-200">
                            <div className="flex justify-between items-start mb-6">
                                <div className="w-12 h-12 bg-red-500/10 text-red-500 rounded-xl flex items-center justify-center">
                                    <AlertTriangle size={24} />
                                </div>
                                <button onClick={() => setTeamToLeave(null)} className="text-slate-500 hover:text-white transition-colors p-1">
                                    <X size={20} />
                                </button>
                            </div>
                            <h3 className="text-2xl font-black text-white mb-2 italic uppercase">{t('teamSelection.leaveOperation')}</h3>
                            <p className={cn("text-slate-400 font-medium", teamToLeave.willDelete ? "mb-4" : "mb-8")}>{t('teamSelection.leaveConfirmation', { teamName: teamToLeave.name })}</p>
                            {teamToLeave.willDelete && (
                                <p className="text-red-400/90 text-sm font-bold mb-8 bg-red-500/10 border border-red-500/25 rounded-xl px-4 py-3">
                                    No legal owner has claimed this team and you are the last joined member — leaving will permanently delete it.
                                </p>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                                <button onClick={() => setTeamToLeave(null)} className="py-4 px-6 bg-slate-800 hover:bg-slate-700 text-white font-black rounded-xl transition-all uppercase tracking-widest text-xs" disabled={isLeaving}>{t('common.cancel')}</button>
                                <button onClick={handleLeaveTeam} className="py-4 px-6 bg-red-600 hover:bg-red-500 text-white font-black rounded-xl transition-all uppercase tracking-widest text-xs shadow-lg shadow-red-600/20 active:scale-95" disabled={isLeaving}>
                                    {isLeaving ? t('teamSelection.aborting') : t('teamSelection.confirmExit')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Icon Tooltip */}
                {iconTooltip && (
                    <div className="fixed z-[9999] pointer-events-none" style={{ left: iconTooltip.x + 14, top: iconTooltip.y - 10 }}>
                        <div className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1 shadow-2xl shadow-black/60">
                            <span className={`text-[9px] font-bold uppercase tracking-wider ${iconTooltip.color}`}>{iconTooltip.label}</span>
                        </div>
                    </div>
                )}
            </div>

            {!showWizard && !tunnelTransition && <MainMenuFeeds hasClip={!!clipOfWeek} isAdmin={!!user?.isAdmin}/>}
            <div className="hub-footer">
            {broadcast && !activePanel && !showWizard && !tunnelTransition && (
                <div className="hub-footer-item hub-broadcast bg-amber-500/10 border border-amber-500/30 rounded-xl px-5 py-3 flex items-center gap-4 backdrop-blur-md shadow-[0_0_20px_rgba(245,158,11,0.15)] text-left group">
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0 border border-amber-500/30 group-hover:scale-110 transition-transform">
                        <AlertTriangle size={18} className="text-amber-500" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[10px] font-black uppercase tracking-widest text-amber-500 mb-1">{broadcast.title}</div>
                        <div className="text-[11px] text-amber-400/90 font-medium leading-relaxed">{broadcast.message}</div>
                    </div>
                </div>
            )}
            {/* Weekly Raffle entry — interactive loot-case image, bottom-left */}
            {!showWizard && (
                <div className={`hub-footer-item fixed bottom-6 left-6 z-[200] flex flex-col items-center gap-2 transition-all duration-500 ${tunnelTransition ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100'}`}>
                    <style>{`@keyframes raffle-case-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }`}</style>
                    <button
                        onClick={() => navigate('/raffles')}
                        title="Weekly Raffles — spend your raffle points on skins"
                        className="group relative block active:scale-95 transition-transform"
                        style={{ width: 184, height: 128 }}
                    >
                        {/* soft glow under the case */}
                        <span className="absolute inset-x-6 bottom-2 h-8 rounded-full blur-2xl bg-violet-500/25 group-hover:bg-violet-400/45 transition-colors duration-300" />
                        {/* closed case (default) */}
                        <img
                            src="/images/Stratsync-Case.png"
                            alt="Stratsync Case"
                            className="absolute inset-0 w-full h-full object-contain transition-opacity duration-300 group-hover:opacity-0 drop-shadow-[0_6px_16px_rgba(0,0,0,0.5)]"
                            style={{ animation: 'raffle-case-bob 3.4s ease-in-out infinite' }}
                        />
                        {/* open case (on hover) */}
                        <img
                            src="/images/stratsync-case-open.png"
                            alt="Stratsync Case — open the weekly raffles"
                            className="absolute inset-0 w-full h-full object-contain opacity-0 transition-opacity duration-300 group-hover:opacity-100 drop-shadow-[0_6px_20px_rgba(139,92,246,0.5)]"
                            style={{ animation: 'raffle-case-bob 3.4s ease-in-out infinite' }}
                        />
                    </button>
                    {/* info: raffle points + next-close timer */}
                    <div
                        className="flex flex-col items-center gap-1 px-4 py-2 rounded-2xl border border-violet-500/30 bg-slate-900/85 backdrop-blur-sm"
                        style={{ boxShadow: '0 0 22px rgba(139,92,246,0.18), 0 4px 12px rgba(0,0,0,0.35)' }}
                    >
                        <span className="text-[9px] font-black uppercase tracking-[0.18em] text-violet-300">Raffles</span>
                        <div className="flex items-center gap-1.5">
                            <RafflePointsIcon size={15} className="text-violet-300" />
                            <span className="text-base font-black text-white leading-none">{rafflePoints ?? '—'}</span>
                            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500">points</span>
                        </div>
                        {nextRaffleCloseAt && (
                            <div className="flex items-center gap-1.5 text-[9.5px] font-bold text-slate-400 mt-0.5">
                                <Clock size={11} className="text-violet-300/80" />
                                <span>Next raffle closes in <span className="text-violet-200">{raffleCountdown}</span></span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ═══ Clip of the Week — winner of the latest release ═══ */}
            {/* Anchored to the bottom right, stacked directly above the buttons
                in that corner. The offset follows the same admin condition the
                browser-extension button uses, so it clears whichever of them is
                showing instead of overlapping one of the two layouts. */}
            {!showWizard && clipOfWeek && (
                <div
                    className={`hub-footer-item hub-clip fixed right-6 z-[200] w-64 overflow-hidden rounded-2xl border border-amber-500/25 bg-slate-950/85 backdrop-blur-sm shadow-lg transition-all duration-500 ${user?.isAdmin ? 'bottom-[9.5rem]' : 'bottom-[6rem]'} ${tunnelTransition ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100'}`}
                    style={{ boxShadow: '0 0 20px rgba(245,158,11,0.08), 0 4px 15px rgba(0,0,0,0.4)' }}
                >
                    <div className="flex items-center gap-1.5 border-b border-amber-500/20 bg-amber-500/[0.07] px-3 py-2">
                        <Trophy size={13} className="text-amber-400" />
                        <span className="text-[9px] font-black uppercase tracking-[0.14em] text-amber-300">Clip of the Week</span>
                    </div>

                    {/* The card plays a muted teaser; the click opens the real thing. */}
                    <button
                        type="button"
                        onClick={() => setClipModalOpen(true)}
                        className="group relative block w-full"
                        title="Watch the clip"
                    >
                        <video
                            src={resolveVideoUrl(clipOfWeek.s3VideoUrl)}
                            muted
                            loop
                            autoPlay
                            playsInline
                            preload="metadata"
                            className="aspect-video w-full bg-black object-cover"
                        />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/35 group-hover:opacity-100">
                            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-black/60">
                                <Play size={16} className="ml-0.5 text-white" />
                            </span>
                        </span>
                    </button>

                    <button
                        type="button"
                        disabled={!clipOfWeek.player.id}
                        onClick={() => clipOfWeek.player.id && navigate(`/profile/${clipOfWeek.player.id}`)}
                        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors enabled:hover:bg-white/[0.04] disabled:cursor-default"
                    >
                        {clipOfWeek.player.avatarUrl ? (
                            <img src={clipOfWeek.player.avatarUrl} alt="" className="h-7 w-7 shrink-0 rounded-full border border-amber-500/25 object-cover" />
                        ) : (
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-amber-500/25 bg-amber-500/10">
                                <User size={13} className="text-amber-300" />
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="truncate text-[11px] font-black text-white">{clipOfWeek.player.name}</div>
                            <div className="truncate text-[9px] text-slate-500">
                                {clipOfWeek.mapName ? `${String(clipOfWeek.mapName).replace(/^de_/, '')} · ` : ''}{clipOfWeek.likeCount} {clipOfWeek.likeCount === 1 ? 'like' : 'likes'}
                            </div>
                        </div>
                        {clipOfWeek.player.id && <ChevronRight size={14} className="shrink-0 text-slate-600" />}
                    </button>
                </div>
            )}

            {/* ═══ Clip of the Week — fullscreen player ═══ */}
            {/* Deliberately chrome-free: the video, a close button, and the
                author. Everything the card already said stays on the card. */}
            {clipOfWeek && clipModalOpen && (
                <div
                    className="fixed inset-0 z-[400] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
                    onMouseDown={(event) => { if (event.target === event.currentTarget) setClipModalOpen(false); }}
                >
                    <div className="relative w-full max-w-5xl">
                        <button
                            type="button"
                            onClick={() => setClipModalOpen(false)}
                            className="absolute -top-11 right-0 flex h-9 w-9 items-center justify-center rounded-lg border border-white/15 bg-slate-900/80 text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
                            aria-label="Close"
                        >
                            <X size={18} />
                        </button>

                        <video
                            src={resolveVideoUrl(clipOfWeek.s3VideoUrl)}
                            controls
                            autoPlay
                            playsInline
                            className="max-h-[80vh] w-full rounded-xl bg-black object-contain"
                        />

                        <button
                            type="button"
                            disabled={!clipOfWeek.player.id}
                            onClick={() => clipOfWeek.player.id && navigate(`/profile/${clipOfWeek.player.id}`)}
                            className="mt-3 flex w-full items-center gap-3 rounded-xl border border-white/10 bg-slate-950/80 px-4 py-3 text-left transition-colors enabled:hover:border-amber-500/30 enabled:hover:bg-slate-900/80 disabled:cursor-default"
                        >
                            {clipOfWeek.player.avatarUrl ? (
                                <img src={clipOfWeek.player.avatarUrl} alt="" className="h-9 w-9 shrink-0 rounded-full border border-amber-500/25 object-cover" />
                            ) : (
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-amber-500/25 bg-amber-500/10">
                                    <User size={16} className="text-amber-300" />
                                </div>
                            )}
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-black text-white">{clipOfWeek.player.name}</div>
                                <div className="truncate text-[10px] text-slate-500">
                                    {clipOfWeek.mapName ? `${String(clipOfWeek.mapName).replace(/^de_/, '')} · ` : ''}{clipOfWeek.likeCount} {clipOfWeek.likeCount === 1 ? 'like' : 'likes'}
                                </div>
                            </div>
                            {clipOfWeek.player.id && (
                                <span className="flex shrink-0 items-center gap-1 text-[10px] font-black uppercase tracking-wider text-amber-300">
                                    Profile <ChevronRight size={13} />
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* ═══ Floating Admin Command Center Button (outside transform container) ═══ */}
            {!showWizard && user?.isAdmin && (
                <button
                    onClick={() => navigate('/admin')}
                    className="hub-footer-item fixed bottom-6 right-6 z-[200] flex items-center gap-2 px-4 py-3 rounded-xl border border-red-500/30 bg-red-950/80 backdrop-blur-sm text-red-400 hover:text-red-300 hover:bg-red-900/80 hover:border-red-400/50 transition-all active:scale-95 shadow-lg shadow-red-900/30 group"
                    style={{ boxShadow: '0 0 20px rgba(220, 38, 38, 0.15), 0 4px 15px rgba(0,0,0,0.4)' }}
                >
                    <Shield size={18} className="group-hover:scale-110 transition-transform duration-300" />
                    <span className="text-[10px] font-black uppercase tracking-[0.15em]">Admin Command Center</span>
                </button>
            )}
            </div>
        </div>
    );
}
